import React from 'react';
import { BrowserRouter, Router } from 'react-router-dom';
import {
  mountReactComponent,
  unmountReactComponent,
  CoreAuth,
  RemoteLifecyleEventArgs,
  RemoteApplicationAdapter,
} from '@amzn/dots-core-ui';
import '@amzn/meridian-tokens/base/font/amazon-ember.css';
import { QueryClient, QueryClientProvider } from 'react-query';
import AppRouter from './features/AppRouter';
import AppNavigation from './features/AppNavigation';
import App from './features/App';
import { environment } from './environments';
import { ClientProvider } from './api';

declare global {
  interface Window {
    TestManagement: RemoteApplicationAdapter;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // do not trigger a refetch when the window re-focuses
      refetchOnWindowFocus: false,
      // don't retry failed requests, it probably won't help
      retry: 0,
    },
  },
});

window.TestManagement = {
  mount: ({ containerId }: RemoteLifecyleEventArgs): void =>
    mountReactComponent(
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <CoreAuth userManagerSettings={environment.userManagerSettings}>
            <ClientProvider tmsApiUrl={environment.tmsApiUrl}>
              <App />
            </ClientProvider>
          </CoreAuth>
        </BrowserRouter>
      </QueryClientProvider>,
      containerId
    ),
  mountRouter: ({
    containerId,
    history,
    createToken,
  }: RemoteLifecyleEventArgs): void =>
    mountReactComponent(
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <ClientProvider
            tmsApiUrl={environment.tmsApiUrl}
            createToken={createToken}
          >
            <AppRouter isRootApp={false} />
          </ClientProvider>
        </Router>
      </QueryClientProvider>,
      containerId
    ),
  mountNavigation: ({
    containerId,
    history,
    isMenuOpen,
  }: RemoteLifecyleEventArgs): void =>
    mountReactComponent(
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <AppNavigation isMenuOpen={isMenuOpen} />
        </Router>
      </QueryClientProvider>,
      containerId
    ),
  unmountRouter: unmountReactComponent,
  unmountNavigation: unmountReactComponent,
};
